import { cn } from 'cn';
import { type ReactNode, useId } from 'react';

import { isFieldRequired, unique } from '@vestigas/utils';

import {
  CountrySelect,
  type Option,
  ValidationError,
} from '@vestigas/ui/molecules';

import { useFieldContext } from './form-context';
import { InputLabel } from './InputLabel';

type MultiSelectCountriesFieldProps = {
  readonly formatOptionLabel?: (option: Option) => ReactNode;
  readonly hint?: string;
  readonly isClearable?: boolean;
  readonly isDisabled?: boolean;
  readonly isMultiSelect?: boolean;
  readonly label?: string;
  readonly placeholder?: string;
  readonly testId?: string;
} & ComponentStyling;

export const MultiSelectCountriesField = ({
  className,
  formatOptionLabel,
  hint,
  isClearable = true,
  isDisabled = false,
  isMultiSelect = false,
  label,
  placeholder = 'Land auswählen',
  style,
  testId,
}: MultiSelectCountriesFieldProps) => {
  const field = useFieldContext();

  const isRequired = isFieldRequired(
    field.options.form.options.validators,
    field.name,
  );

  const errors = unique(
    field.state.meta.errors.map(({ message }) => message),
  ).join(', ');
  const hasErrors = Boolean(errors);

  const id = useId();

  return (
    <div className={cn('relative', className)} style={style}>
      {Boolean(label) && (
        <InputLabel hint={hint} htmlFor={id} isRequired={isRequired}>
          {label}
        </InputLabel>
      )}
      <CountrySelect
        className="flex-1"
        errors={errors}
        formatOptionLabel={formatOptionLabel}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isMultiSelect={isMultiSelect}
        placeholder={placeholder}
        testId={testId}
        value={field.state.value ?? []}
        onChange={field.handleChange}
      />
      {hasErrors ? <ValidationError>{errors}</ValidationError> : null}
    </div>
  );
};
